import React, { useState } from 'react';
import { ArrowLeft, Mail, MapPin, Phone, Send, MessageCircle, Clock, CheckCircle, Building, Globe } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSubmitted(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Contact Us | LTV Production - Video Production Company London</title>
        <meta name="description" content="Contact LTV Production for video production services, live streaming, event coverage, and music videos. Located in London, UK. Get a free consultation today." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://ltvproduction.com/contact" />
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_d70cc816-e4d3-4f6e-bfaa-57b96c01f1a7/artifacts/w1f3hxo7_LTV-Production-LOGO-%281%29-white-2.png" 
            alt="LTV Production" 
            className="h-8 w-auto cursor-pointer"
            style={{ filter: 'invert(1)' }}
            onClick={() => navigate('/')}
          />
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-black transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </button>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Ready to bring your vision to life? Get in touch with our team for a free consultation.
          </p>
        </div>
      </section>
      
      {/* Contact Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-xl p-6 text-center border-2 border-gray-200 hover:border-black transition-colors">
              <div className="w-14 h-14 rounded-full bg-black flex items-center justify-center mx-auto mb-4">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-lg mb-2">Email Us</h3>
              <p className="text-gray-600 text-sm mb-3">For general inquiries and quotes</p>
              <a href="mailto:info@ltvproduction.com" className="text-black font-semibold hover:underline">
                info@ltvproduction.com
              </a>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6 text-center border-2 border-gray-200 hover:border-black transition-colors">
              <div className="w-14 h-14 rounded-full bg-black flex items-center justify-center mx-auto mb-4">
                <Phone className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-lg mb-2">Call Us</h3>
              <p className="text-gray-600 text-sm mb-3">Speak directly with our team</p>
              <a href="tel:+442080641808" className="text-black font-semibold hover:underline">
                +44 208 064 1808
              </a>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6 text-center border-2 border-gray-200 hover:border-black transition-colors">
              <div className="w-14 h-14 rounded-full bg-black flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-lg mb-2">Social Media</h3>
              <p className="text-gray-600 text-sm mb-3">Follow us and DM anytime</p>
              <a href="https://instagram.com/ltvproduction" target="_blank" rel="noopener noreferrer" className="text-black font-semibold hover:underline">
                @ltvproduction
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-6">Company Information</h2>
                <p className="text-gray-600 mb-8">
                  LTV Production LTD is a registered company in the United Kingdom, specializing in professional video production services.
                </p>
              </div>
              
              {/* Full Business Details */}
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="font-bold text-xl mb-6 flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Business Details
                </h3>
                
                <div className="space-y-5">
                  <div>
                    <p className="font-semibold text-black">Company Name</p>
                    <p className="text-gray-600">LTV Production LTD</p>
                  </div>
                  
                  <div>
                    <p className="font-semibold text-black">Business Type</p>
                    <p className="text-gray-600">Video Production & Media Services</p>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-black">Registered Address</p>
                      <p className="text-gray-600">LTV Production Studio</p>
                      <p className="text-gray-600">Unit 112</p>
                      <p className="text-gray-600">77 Marsh Wall</p>
                      <p className="text-gray-600">London E14 9SG</p>
                      <p className="text-gray-600">United Kingdom</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-black">Phone</p>
                      <p className="text-gray-600">+44 208 064 1808</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-black">Email Addresses</p>
                      <p className="text-gray-600">General: info@ltvproduction.com</p>
                      <p className="text-gray-600">Support: support@ltvproduction.com</p>
                      <p className="text-gray-600">Business: business@ltvproduction.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-black">Opening Hours</p>
                      <p className="text-gray-600">Monday - Friday: 9:00am - 11:00pm</p>
                      <p className="text-gray-600">Saturday: 10:00am - 11:00pm</p>
                      <p className="text-gray-600">Sunday: 11:00am - 10:00pm</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Globe className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-black">Service Areas</p>
                      <p className="text-gray-600">United Kingdom, United States (NYC, Miami),</p>
                      <p className="text-gray-600">Caribbean, Europe</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Response Time */}
              <div className="bg-black text-white rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4">Response Times</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    General inquiries: 24-48 hours
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Project quotes: Same business day
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Urgent requests: 4-6 hours
                  </li>
                </ul>
              </div>
              
              {/* Social Links */}
              <div>
                <h3 className="font-bold text-lg mb-4">Follow Us</h3>
                <div className="flex gap-3">
                  <a href="https://instagram.com/ltvproduction" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-black hover:bg-gray-800 flex items-center justify-center transition-colors">
                    <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                  <a href="https://tiktok.com/@ltvproduction" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-black hover:bg-gray-800 flex items-center justify-center transition-colors">
                    <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
                  </a>
                  <a href="https://youtube.com/@ltvproduction" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-black hover:bg-gray-800 flex items-center justify-center transition-colors">
                    <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  </a>
                  <a href="https://twitter.com/ltvproduction" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-black hover:bg-gray-800 flex items-center justify-center transition-colors">
                    <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </a>
                </div>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
                <h2 className="text-2xl font-bold mb-2">Send Us a Message</h2>
                <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>
                
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="h-10 w-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">Message Sent!</h3>
                    <p className="text-gray-600 mb-6">
                      Thank you for contacting us. We'll get back to you within 24 hours.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' }); }}
                      className="text-black font-semibold hover:underline"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Full Name *</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none transition-colors"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Email Address *</label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none transition-colors"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold mb-2">Phone Number</label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none transition-colors"
                          placeholder="+44 7xxx xxx xxx"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">Company/Organization</label>
                        <input
                          type="text"
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none transition-colors"
                          placeholder="Your company name"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold mb-2">Subject *</label>
                      <select
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none transition-colors"
                      >
                        <option value="">Select a subject</option>
                        <option value="video-production">Video Production Inquiry</option>
                        <option value="music-video">Music Video Project</option>
                        <option value="live-streaming">Live Streaming Services</option>
                        <option value="event-coverage">Event Coverage</option>
                        <option value="commercial">Commercial/Advertisement</option>
                        <option value="partnership">Partnership Opportunity</option>
                        <option value="quote">Request a Quote</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold mb-2">Message *</label>
                      <textarea
                        required
                        rows={6}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none transition-colors resize-none"
                        placeholder="Tell us about your project..."
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 bg-black hover:bg-gray-800 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="h-5 w-5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="font-bold mb-2">What services do you offer?</h3>
              <p className="text-gray-600 text-sm">We offer video production, music videos, live streaming, event coverage, and commercial advertisements.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="font-bold mb-2">Do you offer free consultations?</h3>
              <p className="text-gray-600 text-sm">Yes! We offer free initial consultations to discuss your project and provide a custom quote.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="font-bold mb-2">What areas do you serve?</h3>
              <p className="text-gray-600 text-sm">We're based in London but serve clients across the UK, USA, Caribbean, and Europe.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="font-bold mb-2">How long does a typical project take?</h3>
              <p className="text-gray-600 text-sm">Project timelines vary based on scope. We'll provide estimated delivery times in your quote.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
